import { Navigate, useLocation } from 'react-router-dom';
import { useAdminAuth } from '@/hooks/useAdmin';
import { useToast } from '@/hooks/use-toast';
import { useEffect, useRef } from 'react';
import { NovaSpinner } from '@/components/nova';

interface AdminRouteProps {
  children: React.ReactNode;
}

/**
 * Gate for /admin routes.
 *
 * Source of truth: `admin_users` table via useAdminAuth (which calls
 * checkIsAdmin). Previously this used `user_roles` which was a
 * second, drifting source of truth. Consolidated to one path so the
 * sidebar Admin link, AdminLayout, and the admin services all agree
 * on whether a user is an admin.
 */
export default function AdminRoute({ children }: AdminRouteProps) {
  const { isAdmin, isLoading } = useAdminAuth();
  const location = useLocation();
  const { toast } = useToast();
  const hasShownToast = useRef(false);

  useEffect(() => {
    if (!isLoading && !isAdmin && !hasShownToast.current) {
      hasShownToast.current = true;
      toast({
        title: 'Access denied',
        description: "You don't have permission to access this page.",
        variant: 'destructive',
      });
    }
  }, [isLoading, isAdmin, toast]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <NovaSpinner size={32} />
          <p className="text-muted-foreground">Checking permissions…</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}
